import { useCallback } from 'react';

import { makeApiLoaded, NOT_YET_LOADED } from 'scenes/common/helpers/apiData';
import { useFunnelState } from 'scenes/common/contexts/quoteFlowContext';

import {
  MotorFunnelState,
  DefaultCoverType,
  MotorFormAnswer,
  ReferenceData,
} from 'scenes/motor/types';
import {
  getPreselectedCoverType,
  getPreselectedInsuranceType,
  isMonthlyPaymentPreselected,
} from 'scenes/motor/selectors';
import { MotorApiResponses } from 'scenes/motor/actions/types';
import {
  setDefaultCoverOption,
  apiResponse,
  setPreselectedPaymentFrequency,
} from 'scenes/motor/actions';
import { useRegoSearch } from 'scenes/motor/hooks/useRegoSearch';
import { PaymentFrequency, StepDirection } from 'scenes/common/types';
import { useConfigActions } from 'scenes/common/hooks/useActions/useConfigActions';

import useQuoteActions from './useQuoteActions';
import useFlowActions from './useFlowActions';
import usePolicyActions from './usePolicyActions';
import { usePaymentActions } from './usePaymentActions';
import { useCoverOptionActions } from './useCoverOptionActions';
import { useCampaignActions } from './useCampaignActions';

const useActions = (
  handleAnswer: (f: MotorFormAnswer) => void,
  referenceData: ReferenceData
) => {
  const funnelState = useFunnelState<MotorFunnelState>();
  const flowActions = useFlowActions(handleAnswer, referenceData);

  const onSetDefaultCoverOption = useCallback(
    (defaultCoverOptions: DefaultCoverType) => {
      if (
        defaultCoverOptions.preselectedCoverType !==
          getPreselectedCoverType(funnelState) ||
        defaultCoverOptions.preselectedInsuranceType !==
          getPreselectedInsuranceType(funnelState)
      ) {
        handleAnswer(setDefaultCoverOption(defaultCoverOptions));
      }
    },
    [handleAnswer, funnelState]
  );

  const onSwitchPaymentFrequency = useCallback(() => {
    handleAnswer(
      setPreselectedPaymentFrequency(
        isMonthlyPaymentPreselected(funnelState)
          ? PaymentFrequency.Annually
          : PaymentFrequency.Monthly
      )
    );
  }, [funnelState, handleAnswer]);

  const regoSearch = useRegoSearch(
    referenceData,
    flowActions.onApiResponse,
    flowActions.onNextIfLocationIs
  );

  const onApiResponse = useCallback(
    (response: Partial<MotorApiResponses>) => {
      handleAnswer(apiResponse(response));
    },
    [handleAnswer]
  );

  const onClearApiErrors = useCallback(() => {
    onApiResponse({
      quoteDetails: funnelState.apiResponses.quoteDetails.data
        ? makeApiLoaded(funnelState.apiResponses.quoteDetails)
        : NOT_YET_LOADED,
    });
  }, [onApiResponse, funnelState.apiResponses]);

  // Updates an initially recoverable error to a non-recoverable
  // state. Eg when the user declines a recoverable error fix.
  const setRecoverableErrorToBlocking = (): MotorFormAnswer => ({
    direction: StepDirection.ApiResponse,
    formValues: {},
    apiResponses: {
      quoteDetails: {
        ...funnelState.apiResponses.quoteDetails,
        errorReason: {
          ...funnelState.apiResponses.quoteDetails.errorReason,
          blockingError: true,
        },
      },
    },
  });

  const onErrorRecoveryDeclined = useCallback(() => {
    handleAnswer(setRecoverableErrorToBlocking());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onApiResponse, funnelState.apiResponses]);

  const quoteActions = useQuoteActions(
    handleAnswer,
    referenceData,
    flowActions.onNextIfLocationIs,
    onApiResponse
  );

  const coverOptionActions = useCoverOptionActions(
    handleAnswer,
    onApiResponse,
    referenceData
  );

  const policyActions = usePolicyActions(
    flowActions.onNextIfLocationIs,
    onApiResponse,
    referenceData
  );

  const paymentActions = usePaymentActions(
    handleAnswer,
    flowActions.onNextIfLocationIs,
    onApiResponse,
    referenceData
  );

  const campaignActions = useCampaignActions(
    funnelState,
    onApiResponse,
    handleAnswer
  );

  const configActions = useConfigActions(onApiResponse);

  return {
    onNext: flowActions.onNext,
    onNextNoAutoFormMerge: flowActions.onNextNoAutoFormMerge,
    onPrevious: flowActions.onPrevious,
    onRestartQuote: flowActions.onRestartQuote,
    onRemoveDriver: flowActions.onRemoveDriver,
    onEditDriver: flowActions.onEditDriver,
    onEditReviewDriver: flowActions.onEditReviewDriver,
    onUpdateDriver: flowActions.onUpdateDriver,
    onEditQuoteDetails: flowActions.onEditQuoteDetails,
    onEditPolicyDetails: flowActions.onEditPolicyDetails,
    onEditCoverDetails: flowActions.onEditCoverDetails,
    regoSearch,
    onSetDefaultCoverOption,
    onClearApiErrors,
    onSwitchPaymentFrequency,
    ...quoteActions,
    ...coverOptionActions,
    ...policyActions,
    ...paymentActions,
    ...campaignActions,
    ...configActions,
    onErrorRecoveryDeclined,
  };
};

export default useActions;
